import type { Recipe, NutritionData, IngredientDetail } from '../types';

/**
 * Parse a quantity string to extract the numeric value
 * Examples: "500g" -> 500, "1.5kg" -> 1.5, "2条" -> 2
 */
export function parseQuantity(amountStr: string): number {
  if (!amountStr) return 0;
  const match = amountStr.match(/(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : 0;
}

export function parseQuantityUnit(amountStr: string): 'g' | 'ml' | null {
  if (!amountStr) return null;
  if (/ml/i.test(amountStr)) return 'ml';
  if (/g/i.test(amountStr)) return 'g';
  return null;
}

/**
 * Calculate nutrition data for a single recipe based on its linked ingredients
 * If a recipe has no linked ingredients, returns null (caller should use estimation)
 */
export function calculateRecipeNutrition(
  recipe: Recipe,
  ingredients: IngredientDetail[]
): NutritionData | null {
  if (!recipe.ingredients || recipe.ingredients.length === 0) {
    return null;
  }

  // Check if any ingredient has a linkedIngredientId
  const hasLinkedIngredients = recipe.ingredients.some(ing => ing.linkedIngredientId);
  if (!hasLinkedIngredients) {
    return null;
  }

  let totalCalories = 0;
  let totalProtein = 0;
  let totalFat = 0;
  let totalCarbs = 0;
  let totalFiber = 0;

  for (const recipeIngredient of recipe.ingredients) {
    // Find the linked ingredient
    const linkedIngredient = ingredients.find(
      ing => ing.id === recipeIngredient.linkedIngredientId || ing.name === recipeIngredient.name
    );

    if (linkedIngredient) {
      const amount = parseQuantity(recipeIngredient.amount);
      const unit = parseQuantityUnit(recipeIngredient.amount);
      if (!amount || !unit || linkedIngredient.unit !== unit) {
        continue;
      }
      const ratio = amount / 100; // Nutrition data is stored per 100g / 100ml

      totalCalories += linkedIngredient.calories * ratio;
      totalProtein += linkedIngredient.nutrition.protein * ratio;
      totalFat += linkedIngredient.nutrition.fat * ratio;
      totalCarbs += linkedIngredient.nutrition.carbs * ratio;
      totalFiber += linkedIngredient.nutrition.fiber * ratio;
    }
  }

  return {
    calories: Math.round(totalCalories),
    protein: Math.round(totalProtein * 10) / 10, // Round to 1 decimal
    fat: Math.round(totalFat * 10) / 10,
    carbs: Math.round(totalCarbs * 10) / 10,
    fiber: Math.round(totalFiber * 10) / 10,
  };
}

/**
 * Calculate nutrition data for a meal (collection of meal items)
 * Uses real data for recipes with linked ingredients, estimation for others
 */
export function calculateMealNutrition(
  mealItems: any[],
  recipes: Recipe[],
  ingredients: IngredientDetail[]
): NutritionData {
  let totalCalories = 0;
  let totalProtein = 0;
  let totalFat = 0;
  let totalCarbs = 0;
  let totalFiber = 0;

  for (const item of mealItems) {
    if (item.recipeId) {
      // Find the recipe
      const recipe = recipes.find(r => r.id === item.recipeId);
      if (recipe) {
        // Try to calculate from real ingredients
        const recipeNutrition = calculateRecipeNutrition(recipe, ingredients);
        if (recipeNutrition) {
          totalCalories += recipeNutrition.calories;
          totalProtein += recipeNutrition.protein;
          totalFat += recipeNutrition.fat;
          totalCarbs += recipeNutrition.carbs;
          totalFiber += recipeNutrition.fiber;
        } else {
          // Fallback to estimation based on calories
          totalCalories += item.calories || 0;
          totalProtein += (item.calories || 0) * 0.05;
          totalFat += (item.calories || 0) * 0.03;
          totalCarbs += (item.calories || 0) * 0.15;
          totalFiber += (item.calories || 0) * 0.015;
        }
      }
    } else {
      // No recipe link, use estimation
      totalCalories += item.calories || 0;
      totalProtein += (item.calories || 0) * 0.05;
      totalFat += (item.calories || 0) * 0.03;
      totalCarbs += (item.calories || 0) * 0.15;
      totalFiber += (item.calories || 0) * 0.015;
    }
  }

  return {
    calories: Math.round(totalCalories),
    protein: Math.round(totalProtein * 10) / 10,
    fat: Math.round(totalFat * 10) / 10,
    carbs: Math.round(totalCarbs * 10) / 10,
    fiber: Math.round(totalFiber * 10) / 10,
  };
}
